import type { SignUpRequest } from "@/services/api/auth";
import type { AuthRole } from "./authConfig";

const SIGN_UP_JOURNEY_DRAFT_KEY = "bfc_sign_up_journey_draft";

export interface SignUpDraftFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  dateOfBirth: string;
  gender: string;
  location: string;
  addressType: string;
  companyName: string;
  linkedin: string;
}

export interface SignUpJourneyDraft {
  role: AuthRole;
  formData: SignUpDraftFormData;
  accountCreated?: boolean;
  questionsCompleted?: boolean;
}

export const EMPTY_SIGN_UP_DRAFT_FORM_DATA: SignUpDraftFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  dateOfBirth: "",
  gender: "",
  location: "",
  addressType: "",
  companyName: "",
  linkedin: "",
};

const isBrowser = () => typeof window !== "undefined";

export const saveSignUpJourneyDraft = (draft: SignUpJourneyDraft) => {
  if (!isBrowser()) {
    return;
  }

  localStorage.setItem(SIGN_UP_JOURNEY_DRAFT_KEY, JSON.stringify(draft));
};

export const getSignUpJourneyDraft = (
  role?: AuthRole | null,
): SignUpJourneyDraft | null => {
  if (!isBrowser()) {
    return null;
  }

  const rawDraft = localStorage.getItem(SIGN_UP_JOURNEY_DRAFT_KEY);

  if (!rawDraft) {
    return null;
  }

  try {
    const parsedDraft = JSON.parse(rawDraft) as Partial<SignUpJourneyDraft> | null;

    if (!parsedDraft?.role || !parsedDraft?.formData) {
      return null;
    }

    if (role && parsedDraft.role !== role) {
      return null;
    }

    return {
      role: parsedDraft.role,
      accountCreated: Boolean(parsedDraft.accountCreated),
      questionsCompleted: Boolean(parsedDraft.questionsCompleted),
      formData: {
        ...EMPTY_SIGN_UP_DRAFT_FORM_DATA,
        ...parsedDraft.formData,
      },
    };
  } catch {
    clearSignUpJourneyDraft();
    return null;
  }
};

export const clearSignUpJourneyDraft = () => {
  if (!isBrowser()) {
    return;
  }

  localStorage.removeItem(SIGN_UP_JOURNEY_DRAFT_KEY);
};

const getRoleId = (role: AuthRole) => {
  switch (role) {
    case "founder":
      return 2;
    case "investor":
      return 3;
    default:
      return 1;
  }
};

export const buildSignUpRequest = (
  draft: SignUpJourneyDraft,
): SignUpRequest => ({
  firstName: draft.formData.firstName,
  lastName: draft.formData.lastName,
  email: draft.formData.email,
  password: draft.formData.password,
  mobileNo: draft.formData.phone,
  location: draft.formData.location,
  roleIds: [getRoleId(draft.role)],
  dob: draft.formData.dateOfBirth,
  gender: draft.formData.gender,
  userType: draft.role,
  companyName: draft.formData.companyName,
  ...(draft.formData.linkedin && {
    socialLinks: {
      linkedinProfileUrl: draft.formData.linkedin,
    },
  }),
});
